export default {
  name: 'BaseSelect',

  componentName: 'BaseSelect',

  inject: {
    elForm: {
      default: ''
    },

    elFormItem: {
      default: ''
    },

    search: {
      default: ''
    }
  },

  props: {
    /**
     * 绑定数值
     */
    value: '',
    /**
     * 下拉列表数据源
     */
    options: {
      type: [Array, Function],
      required: true
    },
    /**
     * 数据格式化方法
     */
    valueFormat: {
      type: [String, Function],
      validator (value) {
        if (typeof value === 'string') return ['number', 'string'].includes(value)
        return true
      }
    },
    /**
     * 默认值
     */
    defaultValue: null,
    /**
     * 默认值取数据源第一项
     */
    defaultFirstValue: Boolean,
    /**
     * 默认值取数据源最后一项
     */
    defaultLastValue: Boolean,
    /**
     * 组件宽度
     */
    width: [ Number, String ],
    /**
     * 延迟加载数据源
     */
    lazy: Boolean,
    /**
     * 请求参数键名列表
     * 数组表示是请求数据源方法使用多个参数传入
     * 逗号分隔的字符串表示参数是对象
     * 参数后可添加问号表示该参数是可选
     * 需要注意，此属性需要组件在form表单中使用才能生效
     */
    fields: [String, Array],
    /**
     * 请求参数
     * 在fields存在的时候不生效
     */
    data: null
  },

  computed: {
    keys () {
      return this.fieldList.map(key => key.replace(/\?$/, ''))
    },

    fieldList () {
      if (!this.fields) return []
      return Array.isArray(this.fields) ? this.fields : this.fields.split(',')
    },

    elOptions () {
      return Array.isArray(this.list) ? this.list.map(attrs => <el-option { ...{ attrs } }/>) : []
    },

    model () {
      if (this.elForm) return this.elForm.model
      return {}
    }
  },

  watch: {
    options: {
      async handler (value, oldValue) {
        await this.setOptions(value, this.createArgs(this.model), !oldValue && this.lazy)
        this.setDefault()
      },
      immediate: true,
      deep: true
    },

    model: {
      handler: 'requestOptions',
      deep: true
    },

    data: {
      async handler (value, oldValue) {
        await this.setOptions(this.options, [value], !oldValue && this.lazy)
      },
      deep: true
    }
  },

  created () {
    this.$on('resetField', this.setDefault)
  },

  data () {
    return {
      list: []
    }
  },

  methods: {
    format (value) {
      if (Array.isArray(value)) return value.map(this.format)
      if (typeof this.valueFormat === 'function') return this.valueFormat(value)
      if (typeof this.valueFormat === 'string') {
        switch (this.valueFormat) {
          case 'string':
            return String(value)
          case 'number':
            return Number(value)
          default:
            return value
        }
      }
      return value
    },

    /**
     * 验证数值是否存在数组中
     * @param {any} value 数值
     * @param {array} data  数据数组
     */
    validateValue (value, data) {
      if (Array.isArray(data)) return data.some(item => this.format(item.value) === this.format(value))
      return false
    },

    /**
     * 校验参数是否合法
     * @param {any} value
     */
    validateArg (value) {
      return value !== undefined && value !== null && value !== ''
    },

    validateParameter (keys) {
      for (const key of keys) {
        if (this.validateArg(this.model[key])) continue
        return false
      }
      return true
    },

    createArgs (model) {
      if (!this.fields) return []
      const fields = this.keys
      /**
       * 原始传入的fields为数组时，表示请求数据的方法是多参数形式调用
       */
      if (Array.isArray(this.fields)) {
        return this.keys.map(k => model[k])
      }
      /**
       * 多个参数转为对象形式
       */
      if (/,/.test(this.fields)) {
        return [fields.reduce((params, k) => {
          params[k] = model[k]
          return params
        }, {})]
      }
      const key = this.fields.replace(/\?$/, '')
      return [
        model[key]
      ]
    },

    /**
     * 格式化数值
     * 不存在的数据使用defaultValue替代，
     * 已存在但类型不匹配的数据自动转换成相同的类型
     * @param {any} value 原始的数值
     * @param {array} list 数据数组
     */
    formatValue (value, list) {
      if (Array.isArray(value)) {
        // eslint-disable-next-line no-sequences
        return value.reduce((acc, value) => (this.validateValue(value, list) && acc.push(value), acc), [])
      }
      return this.validateValue(this.value, list) ? this.format(this.value) : this.defaultValue
    },

    /**
     * 用于监听参数变化发起请求
     * @param {object} model 参数
     */
    async requestOptions (model) {
      if (!this.fields) return
      const fields = this.fieldList
      const optional = fields.some(i => /\?$/.test(i))
      const args = this.keys.map(k => model[k])
      /**
       * 如果与上一次的参数值一致，直接返回
       */
      if (this.lastArgs && !args.some((item, i) => item !== this.lastArgs[i])) return
      /**
       * 保存参数，用于判断两次参数是否一致，一致就不重复请求数据
       */
      this.lastArgs = args
      /**
       * 如果存在可选的参数则不做参数校验
       */
      let valid = optional || this.validateParameter(this.keys)
      await this.setOptions(valid ? this.options : [], this.createArgs(model))
      this.setDefault()
    },

    /**
     * 处理options
     * @param {array|Function} options 数据数组
     * @param {array} args 参数数组
     * @param {boolean} delay 是否延迟加载
     */
    async setOptions (options, args, delay) {
      const _args = Array.isArray(args) && args.length > 0 ? args : [this.data]
      const value = this.format(this.value)
      const list = typeof options === 'function' ? (delay ? [] : await options(..._args)) : options
      this.list = list
      const newValue = this.formatValue(value, list)
      if (this.value === newValue) return
      this.$emit('input', newValue)
    },

    /**
     * 设置默认值
     * @param {array} list 数据数组
     */
    setDefault () {
      const list = this.list
      if (list.length > 0) {
        const index = this.defaultFirstValue ? 0 : this.defaultLastValue ? (list.length - 1) : -1
        if (index > -1) {
          const { value } = list[index]
          const newValue = this.validateValue(value, list) ? this.format(value) : this.defaultValue
          this.$emit('input', newValue)
          return false
        }
      }
      this.$emit('input', this.defaultValue)
    },

    clear () {
      this.$emit('input', this.defaultValue)
    }
  },

  render (h) {
    const { input, ...listeners } = this.$listeners
    const attrs = {
      props: this.$attrs,
      on: {
        input: e => {
          if (e === '' || e === undefined) return this.clear()
          input && input(e)
        },
        ...listeners
      }
    }
    const style = {
      width: (typeof this.width === 'number' || /^\d+$/.test(this.width)) ? `${this.width}px` : this.width
    }
    return <el-select value={this.value} style={style} { ...attrs }>{this.elOptions}</el-select>
  }
}
